/**
 * B2 (V1, Joey's Gate 2 decision): CSV exports for accountants — invoices,
 * payments, time logs and materials used. Not an accounting integration.
 *
 * Security model (no new database objects):
 * - Every export checks an EXISTING capability before it queries, and every
 *   query is organization-scoped (applyOrganizationScopeForActor) on top of
 *   the existing RLS policies, so a user only ever exports rows their own
 *   organization and role already let them read in the app.
 * - Invoices / payments: invoice.view (loadInvoices, org scoped).
 * - Time logs with labour cost: payroll.export + financial.actual.view, and a
 *   manager also needs the payroll_access flag (docs/permissions_matrix.md).
 * - Materials used with cost: material.usage.view + financial.actual.view.
 *
 * Money uses the app's existing figures (summarizeInvoiceSettlement,
 * normalizeLogHours, computeLineLabourCost, project_material_usage.total_cost);
 * no new financial formulas. Amounts are CAD with two decimals.
 *
 * Spreadsheet safety: text cells that start with = + - @ tab or CR are
 * prefixed with an apostrophe (OWASP CSV injection guidance), cells are
 * RFC 4180 quoted, rows end in CRLF and the file starts with a UTF-8 BOM so
 * Excel opens accents (French names) correctly.
 */
import { supabase } from "../supabase";
import { assertCan, can, CAPABILITY, isAdmin } from "./permissions";
import { applyOrganizationScopeForActor } from "./orgQueryScope";
import { applyActiveTimelogFilter } from "./timeLogVisibility";
import { computeLineLabourCost, normalizeLogHours } from "./labourCost";
import { centsToDecimal, summarizeInvoiceSettlement, toCents } from "./invoiceMoney";
import { resolveInvoiceDate, resolveInvoiceDueDate } from "./invoicePaymentTerms";
import { formatInvoiceStatus, loadInvoices } from "./invoices";
import { formatSupabaseError } from "./estimates";
import { sanitizeFilenamePart } from "../pdf/shared/reportPdfFilenames";

export const CSV_EXPORT = Object.freeze({
  INVOICES: "invoices",
  PAYMENTS: "payments",
  TIME: "time",
  MATERIALS: "materials",
});

export const CSV_BOM = "\uFEFF";
const FORMULA_TRIGGER = /^[=+\-@\t\r]/;

/** One CSV cell. Numbers stay numeric; text is neutralised and quoted. */
export function csvCell(value) {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : "";
  }
  let text = String(value);
  if (FORMULA_TRIGGER.test(text)) {
    text = `'${text}`;
  }
  if (/[",\r\n]/.test(text) || /^\s|\s$/.test(text)) {
    text = `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

/** columns: [{ header, value(row) }] → CSV text with BOM and CRLF. */
export function buildCsv(columns, rows = []) {
  const lines = [columns.map((c) => csvCell(c.header)).join(",")];
  for (const row of rows) {
    lines.push(columns.map((c) => csvCell(c.value(row))).join(","));
  }
  return `${CSV_BOM}${lines.join("\r\n")}\r\n`;
}

/** Money as a plain CAD number with two decimals (spreadsheet sums it). */
export function money(value) {
  if (value === null || value === undefined || value === "") return null;
  const cents = toCents(value);
  return Number(centsToDecimal(cents));
}

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const isoDay = (value) => (value ? String(value).slice(0, 10) : "");

function inRange(day, { from, to } = {}) {
  if (!day) return !from && !to;
  if (from && day < from) return false;
  if (to && day > to) return false;
  return true;
}

function wrap(error) {
  const wrapped = new Error(formatSupabaseError(error));
  wrapped.cause = error;
  return wrapped;
}

// ---------- capabilities ----------

export function canExportInvoices(actor) {
  return can(actor, CAPABILITY.INVOICE_VIEW);
}

export function canExportTime(actor, orgPolicies) {
  return (
    can(actor, CAPABILITY.PAYROLL_EXPORT, {}, orgPolicies) &&
    can(actor, CAPABILITY.FINANCIAL_ACTUAL_VIEW) &&
    (isAdmin(actor) || actor?.payroll_access === true)
  );
}

export function canExportMaterials(actor) {
  return (
    can(actor, CAPABILITY.MATERIAL_USAGE_VIEW) &&
    can(actor, CAPABILITY.FINANCIAL_ACTUAL_VIEW)
  );
}

export function availableCsvExports(actor, orgPolicies) {
  const list = [];
  if (canExportInvoices(actor)) list.push(CSV_EXPORT.INVOICES, CSV_EXPORT.PAYMENTS);
  if (canExportTime(actor, orgPolicies)) list.push(CSV_EXPORT.TIME);
  if (canExportMaterials(actor)) list.push(CSV_EXPORT.MATERIALS);
  return list;
}

function denied() {
  const error = new Error("You do not have access to this export.");
  error.code = "CP_EXPORT_FORBIDDEN";
  return error;
}

// ---------- columns ----------

export const INVOICE_COLUMNS = Object.freeze([
  { header: "Invoice number", value: (i) => i.invoice_number },
  { header: "Status", value: (i) => formatInvoiceStatus(i.status) },
  { header: "Invoice date", value: (i) => resolveInvoiceDate(i) || "" },
  { header: "Due date", value: (i) => resolveInvoiceDueDate(i) || "" },
  { header: "Client", value: (i) => i.clients?.client_name || "" },
  { header: "Project number", value: (i) => i.projects?.project_number || "" },
  { header: "Project", value: (i) => i.projects?.project_name || "" },
  { header: "Title", value: (i) => i.title || "" },
  { header: "Client reference", value: (i) => i.client_reference || "" },
  { header: "Subtotal (CAD)", value: (i) => money(i.subtotal) },
  { header: "Tax (CAD)", value: (i) => money(i.total_tax) },
  { header: "Total (CAD)", value: (i) => money(i.total_amount) },
  { header: "Paid (CAD)", value: (i) => Number(centsToDecimal(summarizeInvoiceSettlement(i).paidCents)) },
  { header: "Balance (CAD)", value: (i) => Number(centsToDecimal(summarizeInvoiceSettlement(i).outstandingCents)) },
]);

export const PAYMENT_COLUMNS = Object.freeze([
  { header: "Payment date", value: (p) => isoDay(p.payment_date) },
  { header: "Invoice number", value: (p) => p.invoice?.invoice_number || "" },
  { header: "Client", value: (p) => p.invoice?.clients?.client_name || "" },
  { header: "Project", value: (p) => p.invoice?.projects?.project_name || "" },
  { header: "Type", value: (p) => (p.payment_role === "reversal" ? "Reversal" : "Receipt") },
  { header: "Amount (CAD)", value: (p) => money(p.amount) },
  { header: "Method", value: (p) => p.payment_method || "" },
  { header: "Reference", value: (p) => p.payment_reference || "" },
  { header: "Notes", value: (p) => p.payment_notes || "" },
  { header: "Payment id", value: (p) => p.payment_id || "" },
  { header: "Reverses payment id", value: (p) => p.reversal_of_payment_id || "" },
]);

export const TIME_COLUMNS = Object.freeze([
  { header: "Date", value: (t) => isoDay(t.date_iso) },
  { header: "Employee", value: (t) => t.employee || "" },
  { header: "Project number", value: (t) => t.job_number || "" },
  { header: "Project", value: (t) => t.project_name || "" },
  { header: "Client", value: (t) => t.client_name || "" },
  { header: "Work category", value: (t) => t.work_category || "" },
  { header: "Trade", value: (t) => t.trade_name || "" },
  { header: "Classification", value: (t) => t.classification_name || "" },
  { header: "Hours", value: (t) => round2(normalizeLogHours(t)) },
  { header: "Hourly rate (CAD)", value: (t) => money(t.hourly_rate) },
  { header: "Labour cost (CAD)", value: (t) => (t.hourly_rate == null ? null : round2(computeLineLabourCost(t))) },
  { header: "Approval status", value: (t) => t.approval_status || "" },
  { header: "Payroll status", value: (t) => t.payroll_status || "" },
]);

export const MATERIAL_COLUMNS = Object.freeze([
  { header: "Usage date", value: (m) => isoDay(m.usage_date) },
  { header: "Project number", value: (m) => m.project?.project_number || "" },
  { header: "Project", value: (m) => m.project?.project_name || "" },
  { header: "Material", value: (m) => m.material_name_snapshot || "" },
  { header: "SKU", value: (m) => m.sku_snapshot || "" },
  { header: "Quantity", value: (m) => (m.quantity == null ? null : Number(m.quantity)) },
  { header: "Unit cost (CAD)", value: (m) => money(m.unit_cost_snapshot) },
  { header: "Total cost (CAD)", value: (m) => money(m.total_cost) },
  { header: "Notes", value: (m) => m.notes || "" },
]);

// ---------- row builders (pure) ----------

export function invoiceRowsForExport(invoices = [], range = {}) {
  return (invoices || [])
    .filter((i) => i && i.status !== "archived")
    .filter((i) => inRange(resolveInvoiceDate(i) || isoDay(i.created_at), range))
    .sort((a, b) => String(a.invoice_number).localeCompare(String(b.invoice_number), undefined, { numeric: true }));
}

export function paymentRowsForExport(invoices = [], range = {}) {
  const rows = [];
  for (const invoice of invoices || []) {
    for (const payment of invoice?.invoice_payments || []) {
      if (!inRange(isoDay(payment.payment_date), range)) continue;
      rows.push({ ...payment, invoice });
    }
  }
  return rows.sort((a, b) => isoDay(a.payment_date).localeCompare(isoDay(b.payment_date)));
}

// ---------- loaders ----------

const TIME_SELECT =
  "id, employee, date_iso, project_id, project_name, job_number, client_name, work_category, trade_name, classification_name, total_hours, net_work_seconds, hourly_rate, approval_status, payroll_status";

async function loadTimeRows(actor, orgPolicies, range) {
  if (!canExportTime(actor, orgPolicies)) throw denied();
  let query = supabase.from("time_logs").select(TIME_SELECT).order("date_iso", { ascending: true });
  if (range.from) query = query.gte("date_iso", range.from);
  if (range.to) query = query.lte("date_iso", range.to);
  query = applyActiveTimelogFilter(query);
  query = await applyOrganizationScopeForActor(query, actor, supabase, { tableName: "time_logs" });
  const { data, error } = await query;
  if (error) throw wrap(error);
  return data || [];
}

async function loadMaterialRows(actor, range) {
  if (!canExportMaterials(actor)) throw denied();
  let query = supabase
    .from("project_material_usage")
    .select("usage_id, project_id, usage_date, material_name_snapshot, sku_snapshot, quantity, unit_cost_snapshot, total_cost, notes")
    .is("voided_at", null)
    .order("usage_date", { ascending: true });
  if (range.from) query = query.gte("usage_date", range.from);
  if (range.to) query = query.lte("usage_date", range.to);
  query = await applyOrganizationScopeForActor(query, actor, supabase, { tableName: "project_material_usage" });
  const { data, error } = await query;
  if (error) throw wrap(error);
  const rows = data || [];

  const ids = [...new Set(rows.map((r) => r.project_id).filter(Boolean))];
  let projects = new Map();
  if (ids.length) {
    let pq = supabase.from("projects").select("project_id, project_name, project_number").in("project_id", ids);
    pq = await applyOrganizationScopeForActor(pq, actor, supabase, { tableName: "projects" });
    const { data: pdata, error: perr } = await pq;
    if (perr) throw wrap(perr);
    projects = new Map((pdata || []).map((p) => [p.project_id, p]));
  }
  return rows.map((r) => ({ ...r, project: projects.get(r.project_id) || null }));
}

export function csvFilename(kind, range = {}, orgName = "") {
  const label = { invoices: "Invoices", payments: "Payments", time: "Time_Logs", materials: "Materials_Used" }[kind] || "Export";
  const org = sanitizeFilenamePart(orgName);
  const period = range.from || range.to ? `${range.from || "start"}_to_${range.to || "today"}` : "All_Dates";
  return `${org ? `${org}_` : ""}CrewPilot_${label}_${period}.csv`;
}

/**
 * Build one export. Returns { filename, csv, rowCount }.
 * @param {"invoices"|"payments"|"time"|"materials"} kind
 */
export async function buildAccountantCsv(kind, { actor, orgPolicies = null, range = {}, orgName = "" } = {}) {
  if (range.from && range.to && range.from > range.to) {
    throw new Error("End date must be on or after the start date.");
  }
  let columns;
  let rows;
  if (kind === CSV_EXPORT.INVOICES || kind === CSV_EXPORT.PAYMENTS) {
    if (!canExportInvoices(actor)) throw denied();
    assertCan(actor, CAPABILITY.INVOICE_VIEW);
    const invoices = await loadInvoices(actor);
    if (kind === CSV_EXPORT.INVOICES) {
      columns = INVOICE_COLUMNS;
      rows = invoiceRowsForExport(invoices, range);
    } else {
      columns = PAYMENT_COLUMNS;
      rows = paymentRowsForExport(invoices, range);
    }
  } else if (kind === CSV_EXPORT.TIME) {
    columns = TIME_COLUMNS;
    rows = await loadTimeRows(actor, orgPolicies, range);
  } else if (kind === CSV_EXPORT.MATERIALS) {
    columns = MATERIAL_COLUMNS;
    rows = await loadMaterialRows(actor, range);
  } else {
    throw new Error("Unknown export.");
  }
  return { filename: csvFilename(kind, range, orgName), csv: buildCsv(columns, rows), rowCount: rows.length };
}

/** Save a CSV file (anchor download; works on desktop, Android Chrome and iOS Safari 13+). */
export function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
    anchor.remove();
  }, 1000);
  return blob;
}
